import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { EntityCrudPageTemplateComponent } from '../../../layout/entity-crud-page-template/entity-crud-page-template.component';
import { DataTableColumnButton, TableColumn, TableLazyEvent } from '../../../shared/contracts/table-column.interface';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { NotificationService } from '../../../shared/services/notification.service';
import { filter, finalize, switchMap, take } from 'rxjs';
import { DialogAction } from '../../../shared/contracts/dialog.interface';
import { ConfirmDialogComponent } from '../../../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { TeamService } from '../../services/team.service';
import {Team} from '../../contracts/team.interface';
import {TeamDialogComponent} from '../dialogs/team-dialog/team-dialog.component';
import {ReactiveFormsModule} from '@angular/forms';

@Component({
  selector: 'app-team-list',
  standalone: true,
  imports: [
    EntityCrudPageTemplateComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './team-list.component.html',
  styleUrls: ['./team-list.component.scss'],
  providers: [DialogService, NotificationService]
})
export class TeamListComponent implements OnInit, AfterViewInit {
  columns: TableColumn[] = [];
  teams: Team[] = [];
  totalRecords = 0;
  rows = 10;
  loading = false;
  private lastLazyEvent: TableLazyEvent | undefined;

  private _refDeleteTeamDialog: DynamicDialogRef | undefined;
  private _refCreateUpdateTeamDialog: DynamicDialogRef | undefined;

  constructor(
    private teamService: TeamService,
    private notificationService: NotificationService,
    private dialogService: DialogService,
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.prepareColumns();
  }

  saveTeam(): void {
    this._refCreateUpdateTeamDialog = this.dialogService.open(TeamDialogComponent, {
      ...TeamDialogComponent.DIALOG_CONFIG,
    });

    this._refCreateUpdateTeamDialog.onClose
      .pipe(
        filter(result => result?.action === DialogAction.OK),
        switchMap(result => this.teamService.create(result.data))
      ).subscribe(() => {
      this.loadTeams(this.lastLazyEvent!);
      this.notificationService.success("Team created successfully.");
    });
  }

  editTeam(team: Team): void {
    this._refCreateUpdateTeamDialog = this.dialogService.open(TeamDialogComponent, {
      ...TeamDialogComponent.DIALOG_CONFIG,
      data: { ...team, user_ids: team.memberIds ?? [] },
    });

    this._refCreateUpdateTeamDialog.onClose
      .pipe(
        filter(result => result?.action === DialogAction.OK),
        switchMap(result => this.teamService.update(team.id, result.data))
      ).subscribe(() => {
      this.loadTeams(this.lastLazyEvent!);
      this.notificationService.success("Team updated successfully.");
    });
  }

  deleteTeam(team: Team): void {
    this._refDeleteTeamDialog = this.dialogService.open(ConfirmDialogComponent, {
      ...ConfirmDialogComponent.DIALOG_CONFIG,
      data: { message: `Are you sure you want to delete team "${team.name}"?` },
    });

    this._refDeleteTeamDialog.onClose
      .pipe(
        filter(result => result?.action === DialogAction.OK),
        switchMap(() => this.teamService.delete(team.id)),
      ).subscribe(() => {
      this.loadTeams(this.lastLazyEvent!);
      this.notificationService.success("Team deleted successfully.");
    });
  }

  deleteSelectedTeams(teams: Team[]): void {
    this.openDeleteTeamsDialog(teams);
  }

  private openDeleteTeamsDialog(teams: Team[]): void {
    this._refDeleteTeamDialog = this.dialogService.open(ConfirmDialogComponent, {
      ...ConfirmDialogComponent.DIALOG_CONFIG,
      data: { message: `Are you sure you want to delete ${teams.length} teams?` },
    });

    this._refDeleteTeamDialog.onClose
      .pipe(filter(result => result?.action === DialogAction.OK))
      .subscribe(() => {});
  }

  loadTeams(event: TableLazyEvent): void {
    this.lastLazyEvent = event;
    this.loading = true;
    this.teamService.getAll(event.page, event.rows, event.filters).pipe(
      take(1),
      finalize(() => this.loading = false)
    ).subscribe(data => {
      this.teams = data.data;
      this.totalRecords = data.total;
    });
  }

  private prepareColumns(): void {
    this.columns = [
      { field: 'name', header: 'Name', sortable: false, filterable: true },
      { field: 'description', header: 'Descrizione', sortable: false },
      { field: 'membersCount', header: 'Numero Membri', sortable: false },
      {
        header: 'Azioni', buttons: <DataTableColumnButton[]>[
          { icon: 'pi-pencil', click: this.editTeam.bind(this), tooltip: 'Modifica', color: 'primary' },
          { icon: 'pi-trash', click: this.deleteTeam.bind(this), tooltip: 'Elimina', color: 'danger' },
        ]
      }
    ];
  }

}
